import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMessage, useUpdateMessage } from '@/hooks/useMessages';
import { useSendMessage } from '@/hooks/useSendMessage';
import { useChatStore } from '@/stores/chatStore';
import ChatThread from './ChatThread';
import ChatInput from './ChatInput';
import MessageBubble from './MessageBubble';
import TypingIndicator from './TypingIndicator';
import Spinner from '@/components/common/Spinner';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';

export default function ChatView() {
  const { id: messageId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNewConversation = !messageId || messageId === 'new';

  const { data: message, isLoading } = useMessage(isNewConversation ? undefined : messageId);
  const updateMessage = useUpdateMessage();
  const { isLoading: isSending, error, messageId: responseMessageId, title, sendMessage, reset } =
    useSendMessage();
  const draft = useChatStore((s) => s.drafts[messageId || 'new'] || '');
  const setDraft = useChatStore((s) => s.setDraft);
  const clearDraft = useChatStore((s) => s.clearDraft);
  const pendingNewMessage = useChatStore((s) => s.pendingNewMessage);
  const clearPendingNewMessage = useChatStore((s) => s.clearPendingNewMessage);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const pendingHandledRef = useRef(false);

  const isRateLimitError = error?.includes('Rate limit exceeded');
  const isFreeTierRateLimit = isRateLimitError && error?.includes('free tier');

  // Auto-send pending message from task detail (or other sources)
  useEffect(() => {
    if (isNewConversation && pendingNewMessage && !pendingHandledRef.current) {
      pendingHandledRef.current = true;
      const messageToSend = pendingNewMessage;
      clearPendingNewMessage();
      handleSend(messageToSend);
    }
  }, [isNewConversation, pendingNewMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark message as read when opened
  useEffect(() => {
    if (message && !message.is_read && message.initiated_by === 'agent') {
      updateMessage.mutate({
        messageId: message.message_id,
        data: { is_read: true },
      });
    }
  }, [message]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigate to the new conversation after it's created
  useEffect(() => {
    if (responseMessageId && isNewConversation) {
      reset();
      navigate(`/app/messages/${responseMessageId}`, { replace: true });
    }
  }, [responseMessageId, isNewConversation, navigate, reset]);

  // Auto-scroll to bottom when conversation updates or loading state changes
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [isSending, error, message?.conversation]);

  const handleSend = async (content: string) => {
    const draftKey = messageId || 'new';
    clearDraft(draftKey);
    setPendingUserMessage(content);

    if (isNewConversation) {
      await sendMessage(content);
    } else {
      await sendMessage(content, messageId);
    }
  };

  const handleRetry = () => {
    if (draft) {
      handleSend(draft);
    }
  };

  const handleArchive = () => {
    if (!messageId || isNewConversation) return;
    updateMessage.mutate(
      { messageId, data: { status: 'archived' } },
      { onSuccess: () => navigate('/app/messages') },
    );
  };

  const handleDelete = () => {
    if (!messageId || isNewConversation) return;
    updateMessage.mutate(
      { messageId, data: { status: 'deleted' } },
      { onSuccess: () => { setShowDeleteConfirm(false); navigate('/app/messages'); } },
    );
  };

  if (isLoading && !isNewConversation) {
    return <Spinner className="py-12" />;
  }

  return (
    <div className="flex h-full flex-col">
      {/* Chat header */}
      <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
        <h2 className="text-lg font-semibold text-neutral-900">
          {title || message?.title || 'New Conversation'}
        </h2>

        {/* Archive & Delete buttons (only for existing conversations) */}
        {!isNewConversation && message && (
          <div className="flex items-center gap-1">
            {/* Archive button */}
            <button
              onClick={handleArchive}
              disabled={updateMessage.isPending}
              className="rounded-lg p-2 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700 disabled:cursor-not-allowed disabled:opacity-50"
              title="Archive"
              aria-label="Archive conversation"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
              </svg>
            </button>

            {/* Delete button */}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={updateMessage.isPending}
              className="rounded-lg p-2 text-neutral-500 transition-colors hover:bg-error-50 hover:text-error-600 disabled:cursor-not-allowed disabled:opacity-50"
              title="Delete"
              aria-label="Delete conversation"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Chat thread */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
        {/* Existing conversation turns */}
        {message?.conversation && <ChatThread conversation={message.conversation} />}

        {/* Optimistic user message while waiting for assistant response */}
        {isSending && pendingUserMessage && (
          <div className="mt-4">
            <MessageBubble
              turn={{
                role: 'user',
                content: pendingUserMessage,
                timestamp: new Date().toISOString(),
              }}
            />
          </div>
        )}

        {/* Loading indicator while waiting for assistant response */}
        {isSending && (
          <div className="mt-4">
            <TypingIndicator />
          </div>
        )}

        {/* Rate limit error: show user message + system response bubble */}
        {isRateLimitError && pendingUserMessage && (
          <div className="mt-4 space-y-4">
            <MessageBubble
              turn={{
                role: 'user',
                content: pendingUserMessage,
                timestamp: new Date().toISOString(),
              }}
            />
            <MessageBubble
              turn={{
                role: 'assistant',
                content:
                  'You have reached the message limit for your current subscription.',
                timestamp: new Date().toISOString(),
              }}
            />
            {isFreeTierRateLimit && (
              <div className="flex justify-start pl-10">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate('/app/subscription')}
                >
                  Upgrade Subscription
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Non-rate-limit error state */}
        {error && !isRateLimitError && (
          <div className="mt-4 rounded-lg bg-error-50 p-4 text-sm text-error-700">
            <p>{error}</p>
            <Button variant="secondary" size="sm" className="mt-2" onClick={handleRetry}>
              Retry
            </Button>
          </div>
        )}
      </div>

      {/* Chat input */}
      <ChatInput
        value={draft}
        onChange={(value) => setDraft(messageId || 'new', value)}
        onSend={handleSend}
        disabled={isSending}
        placeholder={isNewConversation ? 'Start a conversation with Jerry...' : 'Type a reply...'}
      />

      {/* Delete confirmation modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete conversation?"
        size="sm"
      >
        <p className="mb-4 text-sm text-neutral-600">
          This conversation will be removed from your messages. This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            loading={updateMessage.isPending}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}
